import { useEffect, useState } from 'preact/hooks';
import { Artwork } from '../components/Artwork';
import { Icon } from '../components/Icon';
import { SkeletonEpisodeList } from '../components/Skeleton';
import { formatTime, playEpisode } from '../state/player';
import { moveInQueue, queueItems, queueLoading, refreshQueue, removeFromQueue } from '../state/queue';
import { isSignedIn, sessionLoading } from '../state/session';

export function Queue() {
  const [dragging, setDragging] = useState<number | null>(null);

  useEffect(() => {
    if (!sessionLoading.value && isSignedIn.value) {
      void refreshQueue();
    }
  }, [sessionLoading.value, isSignedIn.value]);

  if (sessionLoading.value) {
    return (
      <div class="view">
        <header class="view__header">
          <h1>Queue</h1>
        </header>
        <SkeletonEpisodeList count={4} label="Loading queue" />
      </div>
    );
  }

  if (!isSignedIn.value) {
    return (
      <div class="view">
        <h1>Queue</h1>
        <p class="muted">Sign in to build a play queue that syncs across your devices.</p>
      </div>
    );
  }

  const items = queueItems.value;

  return (
    <div class="view">
      <header class="view__header">
        <h1>Queue</h1>
        <p class="lede">Drag to reorder, or use the arrow buttons.</p>
      </header>

      {queueLoading.value && items.length === 0 && (
        <SkeletonEpisodeList count={4} label="Loading queue" />
      )}

      {!queueLoading.value && items.length === 0 && (
        <div class="empty">
          <h2>Your queue is empty</h2>
          <p>Add episodes from any show and they will line up here.</p>
        </div>
      )}

      <ol class="queue-list">
        {items.map((item, index) => (
          <li
            key={item.episode.id}
            class={`queue-item ${dragging === item.episode.id ? 'is-dragging' : ''}`}
            draggable
            onDragStart={() => setDragging(item.episode.id)}
            onDragEnd={() => setDragging(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (dragging !== null && dragging !== item.episode.id) {
                void moveInQueue(dragging, index);
              }
              setDragging(null);
            }}
          >
            <span class="queue-item__index" aria-hidden="true">
              {index + 1}
            </span>
            <Artwork src={item.episode.imageUrl ?? item.episode.feedImageUrl} alt="" size={48} />

            <div class="queue-item__body">
              <a class="queue-item__title" href={`/episode/${item.episode.id}`}>
                {item.episode.title}
              </a>
              <span class="muted">
                {item.episode.feedTitle} · {formatTime(item.episode.durationSec)}
              </span>
            </div>

            <div class="queue-item__actions">
              <button
                type="button"
                class="icon-button"
                onClick={() => playEpisode(item.episode)}
                aria-label={`Play ${item.episode.title}`}
              >
                <Icon name="play" size={18} />
              </button>
              {/* Keyboard- and screen-reader-accessible equivalent of the drag handle. */}
              <button
                type="button"
                class="icon-button"
                disabled={index === 0}
                onClick={() => void moveInQueue(item.episode.id, index - 1)}
                aria-label={`Move ${item.episode.title} up`}
              >
                ↑
              </button>
              <button
                type="button"
                class="icon-button"
                disabled={index === items.length - 1}
                onClick={() => void moveInQueue(item.episode.id, index + 1)}
                aria-label={`Move ${item.episode.title} down`}
              >
                ↓
              </button>
              <button
                type="button"
                class="icon-button"
                onClick={() => void removeFromQueue(item.episode.id)}
                aria-label={`Remove ${item.episode.title} from queue`}
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
